/**
 * An incremental `text/event-stream` parser.
 *
 * Split out from the transport because it is the part with the fiddly rules,
 * and the only part worth testing on its own: frames arrive split across
 * arbitrary chunk boundaries, so the parser has to hold state between pushes.
 */

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

export interface SseParser {
  /** Feed a decoded chunk; returns the frames it completed. */
  push: (chunk: string) => SseMessage[];
}

export function createSseParser(): SseParser {
  let buffer = '';

  return {
    push(chunk: string): SseMessage[] {
      // Normalise line endings first: the spec allows CRLF, CR or LF, and a
      // stream that mixed them would otherwise split frames in the wrong place.
      buffer += chunk.replace(/\r\n?/g, '\n');

      const messages: SseMessage[] = [];
      let boundary = buffer.indexOf('\n\n');

      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const parsed = parseFrame(frame);
        if (parsed) messages.push(parsed);

        boundary = buffer.indexOf('\n\n');
      }

      return messages;
    },
  };
}

function parseFrame(frame: string): SseMessage | undefined {
  let event = 'message';
  let id: string | undefined;
  const data: string[] = [];

  for (const line of frame.split('\n')) {
    // A line starting with a colon is a comment — the heartbeat rides on these.
    if (line.startsWith(':') || line.length === 0) continue;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // Exactly one leading space is part of the framing, not of the value.
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');

    if (field === 'event') event = value;
    else if (field === 'id') id = value;
    else if (field === 'data') data.push(value);
  }

  if (data.length === 0) return undefined;
  return { event, data: data.join('\n'), ...(id === undefined ? {} : { id }) };
}
